/* ============================================================================
   MALSERA — ms-acordeon.js
   Apertura y cierre animados de los desplegables de la ficha.

   DOS FAMILIAS, UNA SOLA ANIMACIÓN (11-10-2026): los acordeones de la
   columna de compra y del FAQ (.ms-accordion, filas con filete) y las
   preguntas del bolso (.ms-pregunta, tarjetas con borde). Se ven distintos a
   propósito, pero se abren igual: el gesto es el mismo aunque la caja no lo
   sea, y quien pulsa no tiene por qué notar que son dos componentes.

   Los acordeones siguen siendo <details> nativos: sin este archivo se abren y
   se cierran igual, solo que en seco. Esto es una capa encima, no una
   dependencia.

   POR QUÉ HAY JAVASCRIPT Y NO SOLO CSS. Animar la altura de un <details>
   hasta `auto` solo lo sabe hacer Chrome. En Safari y Firefox la caja saltaba
   de golpe y el contenido aparecía después: dos movimientos que no casan.
   Aquí se mide la altura real y se anima con la Web Animations API, igual en
   todos los navegadores, y la caja y el contenido van con la misma curva.

   EL RECORRIDO, DE PRINCIPIO A FIN:
     1. Al pulsar, el icono se encoge un instante (CSS, :active) y el + empieza
        a girar hacia − en ese mismo momento, no al terminar.
     2. Abrir: la caja crece con la curva de salida de la casa (--ease-salida),
        rápida al principio y suave al final. El contenido baja 16px mientras
        aparece, y sus filas se van encendiendo una detrás de otra.
     3. Cerrar: el contenido se apaga primero, rápido, y la caja se recoge
        detrás. Cerrar es más corto que abrir: nadie quiere esperar a que algo
        se vaya.
     4. Si se vuelve a pulsar a mitad de camino, la animación da la vuelta
        desde donde está, sin saltar al principio.

   Solo se animan altura, opacidad y transform. La altura es la única
   propiedad que obliga a recalcular el diseño, y aquí es inevitable: es
   justo lo que hay que mover.

   TIEMPOS, acortados el 22-09-2026. La primera versión abría en 520ms y se
   sentía pesada en el móvil: medio segundo es mucho para un gesto que
   responde a un dedo. 380ms al abrir y 260ms al cerrar se leen igual de
   suaves y responden antes.

   Con «reducir movimiento» activado no se intercepta nada: el <details> se
   comporta de forma nativa.

   Lo cargan la ficha y las preguntas frecuentes. Va envuelto y con guarda
   para que dos etiquetas <script> no registren el clic dos veces.
   ============================================================================ */

(() => {
  if (window.msAcordeonListo) return;
  window.msAcordeonListo = true;

  const reducido = window.matchMedia('(prefers-reduced-motion: reduce)');

  // La curva de la casa. Se lee del CSS para que haya una sola fuente.
  const curvaSalida =
    getComputedStyle(document.documentElement).getPropertyValue('--ease-salida').trim() ||
    'cubic-bezier(0, 0, 0.3, 1)';

  const ABRIR = 380;
  const CERRAR = 260;
  const APAGAR = 140;
  const ESCALON = 32; // entre fila y fila al encenderse
  const MAX_FILAS = 8; // a partir de ahí, todas a la vez: la cascada no puede hacerse eterna

  // Avisa al CSS de que la animación la lleva el script, para que apague la
  // versión solo-CSS y no se animen las dos cosas a la vez.
  document.documentElement.classList.add('ms-acordeon-js');

  /** Qué hay dentro del panel que merezca encenderse por turnos. */
  function filasDe(panel) {
    const filas = panel.querySelectorAll(
      // El último selector es para las preguntas del bolso: metafield_tag
      // envuelve la respuesta en un .metafield-rich_text_field, así que los
      // párrafos cuelgan de ahí y no del panel.
      ':scope .ms-accordion__row, :scope.ms-rte > *, :scope > .ms-rte > *, :scope .ms-pair > *, :scope > .metafield-rich_text_field > *'
    );
    return [...filas].slice(0, MAX_FILAS);
  }

  function pararTodo(details, panel) {
    details.getAnimations().forEach((a) => a.cancel());
    panel.getAnimations({ subtree: true }).forEach((a) => a.cancel());
  }

  function terminar(details) {
    details.style.overflow = '';
    delete details.dataset.estado;
  }

  function abrir(details, summary, panel) {
    const desde = details.getBoundingClientRect().height;
    pararTodo(details, panel);

    details.dataset.estado = 'abierto';
    details.style.overflow = 'hidden';
    details.open = true;

    const hasta = details.getBoundingClientRect().height;

    const caja = details.animate(
      { height: [`${desde}px`, `${hasta}px`] },
      { duration: ABRIR, easing: curvaSalida }
    );

    panel.animate(
      [
        { opacity: 0, transform: 'translateY(-16px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      { duration: ABRIR, easing: curvaSalida, delay: 30, fill: 'backwards' }
    );

    const filas = filasDe(panel);
    if (filas.length > 1) {
      filas.forEach((fila, i) => {
        fila.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: 260,
          easing: curvaSalida,
          delay: 60 + i * ESCALON,
          fill: 'backwards',
        });
      });
    }

    caja.onfinish = () => terminar(details);
  }

  function cerrar(details, summary, panel) {
    const desde = details.getBoundingClientRect().height;
    pararTodo(details, panel);

    details.dataset.estado = 'cerrado';
    details.style.overflow = 'hidden';

    // Cerrado mide lo que el resumen más los bordes del propio <details>.
    const bordes = details.offsetHeight - details.clientHeight;
    const hasta = summary.getBoundingClientRect().height + bordes;

    panel.animate(
      [
        { opacity: 1, transform: 'translateY(0)' },
        { opacity: 0, transform: 'translateY(-8px)' },
      ],
      { duration: APAGAR, easing: 'ease-in', fill: 'forwards' }
    );

    const caja = details.animate(
      { height: [`${desde}px`, `${hasta}px`] },
      { duration: CERRAR, easing: 'ease-in-out' }
    );

    caja.onfinish = () => {
      // Primero se cierra de verdad y después se sueltan los estilos del
      // panel: al revés, el contenido parpadearía un fotograma.
      details.open = false;
      panel.getAnimations().forEach((a) => a.cancel());
      terminar(details);
    };
  }

  document.addEventListener('click', (evento) => {
    if (reducido.matches) return;

    const summary = evento.target.closest('.ms-accordion > summary, .ms-pregunta > summary');
    if (!summary) return;

    const details = summary.parentElement;
    const panel = details.querySelector(':scope > .ms-accordion__panel, :scope > .ms-pregunta__panel');
    if (!panel) return;

    evento.preventDefault();

    // A mitad de animación manda hacia dónde iba, no si está `open`: al
    // cerrar, el <details> sigue abierto hasta el final.
    const estaAbriendo = details.dataset.estado
      ? details.dataset.estado === 'abierto'
      : details.open;

    if (estaAbriendo) {
      cerrar(details, summary, panel);
    } else {
      abrir(details, summary, panel);
    }
  });
})();
